"use client";

import Script from "next/script";
import { usePostHog } from "posthog-js/react";
import {
	forwardRef,
	memo,
	useEffect,
	useId,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { TURNSTILE_TEST_SITE_KEY } from "@/lib/security/turnstile";

export type TurnstileWidgetHandle = {
	reset: () => void;
};

const TURNSTILE_SCRIPT_SRC =
	"https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Backoff delays (ms) for the recoverable-failure retry ladder — shared by
 * challenge_error, render_error, and load_timeout. 3 retries (4 attempts
 * total) covers Cloudflare's common transient failures without looping
 * forever; halves the user-visible error rate for the common transient case.
 */
const RETRY_BACKOFF_MS = [1_000, 3_000, 8_000];

/**
 * Cloudflare's "time problem" error code: the visitor's device clock is wrong,
 * so every challenge fails until they fix it. Retrying can never succeed. A
 * session on 2026-10-14 (ID, /id/jobs signup modal) hit it 123 times in 14
 * minutes and only ever saw "disable your ad blocker", which is the wrong
 * advice.
 */
const CLOCK_SKEW_ERROR_CODE = "200100";

/**
 * Coarse browser family for `turnstile_widget_error`, derived from
 * `navigator.userAgent`. Only exists to slice error-rate by browser (the
 * 2026-05 `challenge_error` spike was concentrated on Chrome/desktop — see
 * `magic-link-form.tsx`'s OAuth-steer comment — and the event carried no
 * browser dimension to confirm/refute that for other failure modes).
 * Deliberately a handful of small regexes (matches the existing `isChrome()`
 * idiom in `extension-banner.tsx`/`extension-install-nudge.tsx`) rather than a
 * UA-parsing dependency — that would bloat the worker bundle for a
 * best-effort telemetry dimension. Order matters: Edge/Opera/Samsung UAs also
 * contain "Chrome/", and Chrome's UA also contains "Safari/", so the more
 * specific tokens must be checked first.
 */
function getBrowserFamily(): string {
	if (typeof navigator === "undefined" || !navigator.userAgent)
		return "unknown";
	const ua = navigator.userAgent;
	if (/Edg\//.test(ua)) return "edge";
	if (/OPR\/|Opera/.test(ua)) return "opera";
	if (/SamsungBrowser\//.test(ua)) return "samsung_internet";
	if (/Firefox\//.test(ua)) return "firefox";
	if (/CriOS\//.test(ua)) return "chrome"; // Chrome on iOS
	if (/Chrome\//.test(ua)) return "chrome";
	if (/Safari\//.test(ua)) return "safari";
	return "other";
}

type TurnstileTheme = "light" | "dark" | "auto";

/**
 * Reports a terminal (all-retries-exhausted) widget failure to Sentry so a
 * recurrence pages someone instead of sitting quietly in PostHog. Deliberately
 * posts to a tiny API route rather than importing `@sentry/nextjs` directly —
 * see `src/app/api/telemetry/turnstile-error/route.ts` for why a client-side
 * import (static OR dynamic) of the SDK here would blow the Worker bundle
 * budget: this widget mounts on nearly every page. Fire-and-forget — a failed
 * report must never affect the widget's own recovery UI. Only fires in
 * production: dev has no real widget failures worth reporting, and skipping
 * it in tests avoids a real network call from the unit-test suite.
 */
function reportTerminalFailure(payload: {
	reason: TurnstileErrorReason;
	action?: string;
	locale?: string;
	surface?: string;
	errorCode: string | null;
	browserFamily: string;
}) {
	fetch("/api/telemetry/turnstile-error", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
		keepalive: true,
	}).catch(() => undefined);
}

/**
 * Every failure mode the widget can report to its parent via `onError`.
 * `script_load_error` (the `<Script>` tag itself failing to load — blocked by
 * an ad blocker/firewall, a CDN blip) is distinct from `load_timeout` (the
 * script loaded fine but `window.turnstile` never showed up within
 * `loadTimeoutMs`) — keeping them separate keeps the `reason` honest for any
 * consumer that buckets failures by cause. `clock_skew` is a challenge error
 * with Cloudflare code 200100 (wrong device clock): terminal on the first hit,
 * because only the visitor can fix it.
 */
export type TurnstileErrorReason =
	| "missing_sitekey"
	| "load_timeout"
	| "render_error"
	| "challenge_error"
	| "script_load_error"
	| "clock_skew";

/**
 * Which branch the widget's mount effect actually took, exposed via
 * `data-turnstile-status` on the wrapper `<div>` so e2e/prod probes can
 * assert the app's own contract (a stable, app-controlled handle) instead of
 * scraping Cloudflare's vendor DOM (`input[name="cf-turnstile-response"]`,
 * the `<script>` tag) — see `tests/e2e/CLAUDE.md` and
 * `tests/e2e/production/daily-loop-probe.prod.spec.ts`'s Stage 0.
 * `"rendered"` is the concrete, code-grounded proxy for "sitekey resolved
 * non-empty and Cloudflare's `turnstile.render()` succeeded" — the exact
 * condition that pre-step needs.
 */
export type TurnstileWidgetStatus =
	| "idle"
	| "missing_sitekey"
	| "rendered"
	| "error";

type Props = {
	/**
	 * Cloudflare Turnstile site key. Prefer passing this from a server
	 * component that read it from the runtime Cloudflare env — that path
	 * survives a build that didn't have `NEXT_PUBLIC_TURNSTILE_SITE_KEY`
	 * available for build-time inlining. Falls back to the build-time inline
	 * if omitted.
	 */
	siteKey?: string;
	/** Optional action label echoed back by Cloudflare for telemetry. */
	action?: string;
	/** Visual theme. Defaults to `auto` (matches the user's OS preference). */
	theme?: TurnstileTheme;
	/** Render the smaller "compact" widget if true. */
	compact?: boolean;
	/** Called with the freshly issued token (or null on expiry). */
	onToken?: (token: string | null) => void;
	/**
	 * Called when the widget cannot complete a challenge: the script failed to
	 * load (blocked by extension/network/firewall), Cloudflare returned an
	 * error, the site key is missing/invalid, or the load timeout elapsed.
	 * Lets the parent surface a recovery path to the user instead of leaving
	 * the submit button silently disabled.
	 */
	onError?: (reason: TurnstileErrorReason) => void;
	/**
	 * Milliseconds to wait for the widget to mount before declaring failure.
	 * Defaults to 10s — generous enough for slow networks, short enough that
	 * a user with the script blocked sees a clear error rather than waiting
	 * forever on a disabled button.
	 */
	loadTimeoutMs?: number;
	/**
	 * How many automatic retries (of the 1s/3s/8s backoff ladder) to spend
	 * before surfacing the terminal error to the parent. Defaults to the full
	 * ladder (3). Interactive auth forms should pass a smaller number: the full
	 * ladder kept signup users staring at "Verifying you're human…" for 40–80s
	 * before the soft-fail escape unlocked (2026-07-14 audit) — they bail long
	 * before. A checkout that renders below the fold can afford the full ladder.
	 */
	maxRetryAttempts?: number;
	className?: string;
	/**
	 * Active UI locale (e.g. "id" | "en"). Attached to every `turnstile_widget_error`
	 * so failures can be sliced by locale — the error spike was concentrated in one
	 * locale/region but the event carried no locale dimension to confirm it.
	 */
	locale?: string;
	/**
	 * Which product surface mounted this widget (e.g. "signup", "login",
	 * "checkout"). Attached to every `turnstile_widget_error` so a
	 * load-failure spike on one surface (e.g. signup) is measurable without
	 * being averaged away by every other page that also mounts this widget —
	 * `action` alone is often a fixed literal shared across surfaces (e.g.
	 * magic-link-form.tsx passes the same `action="magic-link"` for both
	 * /login and /signup).
	 */
	surface?: string;
};

declare global {
	interface Window {
		turnstile?: {
			render: (
				container: string | HTMLElement,
				options: {
					sitekey: string;
					action?: string;
					theme?: TurnstileTheme;
					size?: "normal" | "compact";
					appearance?: "always" | "execute" | "interaction-only";
					"refresh-expired"?: "auto" | "manual" | "never";
					callback?: (token: string) => void;
					"expired-callback"?: () => void;
					"error-callback"?: (errorCode?: string) => void;
				},
			) => string;
			reset: (widgetId?: string) => void;
			remove: (widgetId?: string) => void;
		};
	}
}

/**
 * Renders the Cloudflare Turnstile widget. The widget injects an
 * `<input name="cf-turnstile-response">` into the surrounding `<form>` so any
 * submission will include the token automatically — server actions just need
 * to read it from `FormData`.
 *
 * Uses the `?render=explicit` script + manual `turnstile.render()` so we can
 * wire the `onToken` callback (needed for the magic-link flow which doesn't
 * use a native form post).
 *
 * Accepts a ref of type `TurnstileWidgetHandle` to allow parents to call
 * `reset()` and obtain a fresh token after a failed submission. Cloudflare
 * tokens are single-use: once verified server-side the token is spent, even if
 * the overall action failed (e.g. a validation error fires after the bot-check
 * passes). Without an explicit reset the next submit always fails with
 * "bot_check_failed".
 */
const TurnstileWidgetImpl = forwardRef<TurnstileWidgetHandle, Props>(
	function TurnstileWidgetInner(
		{
			siteKey: siteKeyProp,
			action,
			theme = "auto",
			compact = false,
			onToken,
			onError,
			loadTimeoutMs = 10_000,
			maxRetryAttempts = RETRY_BACKOFF_MS.length,
			className,
			locale,
			surface,
		},
		ref,
	) {
		const containerId = useId().replace(/:/g, "_");
		const widgetIdRef = useRef<string | null>(null);
		// Shared across challenge_error / render_error / load_timeout — a single
		// retry ladder for every recoverable failure mode instead of counting
		// challenge_error alone. Persists across effect re-runs within one mount;
		// only a component remount (parent bumps the `key` prop) resets it.
		const retryCountRef = useRef(0);
		const posthog = usePostHog();
		// `usePostHog()` returns the PostHogProvider's context client, which is
		// referentially stable in production (same client instance across
		// renders). Reading it through a ref — kept current via its own tiny
		// effect — instead of listing it as a dependency of the mount effect
		// below means an unstable/re-created client (a misbehaving provider, or
		// a test double that returns a fresh object per call) can't tear down
		// and re-run the whole mount effect — and re-call `turnstile.render()`
		// — on every render; the mount effect should only restart when
		// render-affecting props change, never because of the analytics
		// client's identity.
		const posthogRef = useRef(posthog);
		useEffect(() => {
			posthogRef.current = posthog;
		}, [posthog]);
		const [widgetStatus, setWidgetStatus] =
			useState<TurnstileWidgetStatus>("idle");

		const envKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim();
		const isProd = process.env.NODE_ENV === "production";
		const isDev = process.env.NODE_ENV === "development";
		const siteKey =
			siteKeyProp?.trim() || envKey || (!isProd ? TURNSTILE_TEST_SITE_KEY : "");

		useImperativeHandle(ref, () => ({
			reset() {
				// Dev renders no real widget (see the dev-bypass effect below), so
				// there is nothing to reset and no fresh token would ever arrive:
				// a second single-use submit on the same page (teaser → free review
				// on /tools/cek-ats-cv) stayed disabled forever under `next dev`.
				// Re-issue the bypass token instead, on the next tick so it lands
				// AFTER the caller's own `setToken(null)` that follows reset().
				if (isDev) {
					window.setTimeout(() => onToken?.("dev-bypass"), 0);
					return;
				}
				const ts = window.turnstile;
				if (ts && widgetIdRef.current) {
					try {
						ts.reset(widgetIdRef.current);
					} catch {}
				}
			},
		}));

		// In development, skip the external Cloudflare widget entirely.
		// Server-side verification is also bypassed in dev (see verifyTurnstile).
		useEffect(() => {
			if (!isDev) return;
			onToken?.("dev-bypass");
		}, [isDev, onToken]);

		useEffect(() => {
			if (isDev) return;
			// Refusing to render with an empty sitekey is critical: Cloudflare's
			// script logs `Invalid input for parameter "sitekey"` and silently no-ops
			// the widget, leaving the form's submit button permanently disabled.
			// Surface the failure to the parent immediately so it can show a
			// remediation message instead of stalling forever.
			if (!siteKey) {
				posthogRef.current?.capture?.("turnstile_widget_error", {
					reason: "missing_sitekey",
					action,
					locale,
					surface,
					error_code: null,
					browser_family: getBrowserFamily(),
				});
				setWidgetStatus("missing_sitekey");
				onError?.("missing_sitekey");
				return;
			}

			let cancelled = false;
			let rendered = false;
			const pollTimers: number[] = [];
			const retryTimers: number[] = [];
			const mountedAt = Date.now();
			let timeoutId = 0;
			// Deferred while the tab is hidden — see the visibilitychange listener
			// below. Cloudflare keeps ticking the challenge timer in a backgrounded
			// tab, so an immediate reset-on-expiry there causes a silent
			// issue/expire loop (one idle tab produced 834 expirations).
			let resetPendingOnVisible = false;

			function startLoadTimeout() {
				timeoutId = window.setTimeout(() => {
					if (cancelled || rendered) return;
					handleFailure("load_timeout", {
						wait_ms: Date.now() - mountedAt,
					});
				}, loadTimeoutMs);
			}

			/**
			 * Single retry ladder for every recoverable failure (challenge_error,
			 * render_error, load_timeout). Backoff 1s/3s/8s, capped at
			 * MAX_RETRY_ATTEMPTS retries (4 attempts total including the first) —
			 * covers Cloudflare's common transient failures without looping forever.
			 * Once exhausted, marks the widget cancelled and surfaces the failure.
			 */
			function handleFailure(
				reason: TurnstileErrorReason,
				extra: Record<string, unknown> = {},
			) {
				const attempt = retryCountRef.current;
				const willRetry =
					reason !== "clock_skew" &&
					attempt < Math.min(maxRetryAttempts, RETRY_BACKOFF_MS.length);
				// `error_code` always ships on the event (defaulting to `null`) —
				// only `challenge_error` carries a real Cloudflare-issued code today,
				// but a consistently-present property (rather than sometimes-absent)
				// is what lets a consumer group "no code captured" from "code was
				// null" instead of conflating a missing key with every other reason.
				posthogRef.current?.capture?.("turnstile_widget_error", {
					reason,
					action,
					locale,
					surface,
					auto_retry: willRetry,
					attempt: attempt + 1,
					browser_family: getBrowserFamily(),
					...extra,
					error_code: (extra.error_code as string | null | undefined) ?? null,
				});
				if (willRetry) {
					retryCountRef.current += 1;
					const delay = RETRY_BACKOFF_MS[attempt];
					const id = window.setTimeout(() => {
						if (cancelled) return;
						retryRender(reason);
					}, delay);
					retryTimers.push(id);
					return;
				}
				cancelled = true;
				setWidgetStatus("error");
				if (reason === "clock_skew") {
					// Stop Cloudflare's own auto-retry loop: with a wrong clock it can
					// never pass. "Try again" remounts the widget (parent bumps `key`).
					try {
						const ts = window.turnstile;
						if (ts && widgetIdRef.current) ts.remove(widgetIdRef.current);
					} catch {}
					widgetIdRef.current = null;
				}
				// A wrong device clock is the visitor's setting, not our outage, so
				// it stays out of Sentry (PostHog still records it above).
				if (isProd && reason !== "clock_skew") {
					reportTerminalFailure({
						reason,
						action,
						locale,
						surface,
						errorCode: (extra.error_code as string | null | undefined) ?? null,
						browserFamily: getBrowserFamily(),
					});
				}
				onError?.(reason);
			}

			function retryRender(reason: TurnstileErrorReason) {
				if (reason === "challenge_error") {
					try {
						const ts = window.turnstile;
						if (ts && widgetIdRef.current) ts.reset(widgetIdRef.current);
					} catch {}
					return;
				}
				// render_error / load_timeout: the widget never finished mounting —
				// re-arm the give-up timer and retry the render loop from scratch.
				rendered = false;
				window.clearTimeout(timeoutId);
				startLoadTimeout();
				tryRender();
			}

			function tryRender() {
				if (cancelled) return;
				const ts = window.turnstile;
				const container = document.getElementById(containerId);
				if (!ts || !container) {
					pollTimers.push(window.setTimeout(tryRender, 100));
					return;
				}
				if (widgetIdRef.current) return;
				try {
					widgetIdRef.current = ts.render(container, {
						sitekey: siteKey,
						action,
						theme,
						size: compact ? "compact" : "normal",
						// Stay visually hidden until Cloudflare actually needs to
						// challenge the visitor (invisible mode) — cuts perceived
						// friction on the signup form without changing what the
						// server does with the resulting token.
						appearance: "interaction-only",
						// Cloudflare's own default ("auto") silently re-fetches a new
						// token every ~5 min for as long as the widget stays mounted —
						// including while the tab sits backgrounded, regardless of the
						// visibilitychange guard below. That kept the idle-tab
						// issue/expire loop alive even after deferring OUR OWN reset
						// call on hidden tabs (2026-07-11 fix, #2101): a session on
						// 2026-07-17 still logged 111 expiries over 9+ hours, all
						// `tab_hidden`, on the same ~295s cadence CF's auto-refresh
						// uses. "never" hands control entirely to `expired-callback`
						// below, which already decides exactly when to re-arm.
						"refresh-expired": "never",
						callback: (token) => {
							posthogRef.current?.capture?.("turnstile_token_issued", {
								action,
								time_to_token_ms: Date.now() - mountedAt,
							});
							onToken?.(token);
						},
						"expired-callback": () => {
							posthogRef.current?.capture?.("turnstile_token_expired", {
								action,
								tab_hidden: document.hidden,
							});
							onToken?.(null);
							if (document.hidden) {
								resetPendingOnVisible = true;
								return;
							}
							// Auto-reset to silently fetch a fresh token — prevents the submit
							// button from staying permanently disabled without any visual cue.
							try {
								const ts = window.turnstile;
								if (ts && widgetIdRef.current) ts.reset(widgetIdRef.current);
							} catch {}
						},
						"error-callback": (errorCode?: string) => {
							// Once we have given up, Cloudflare keeps auto-retrying and
							// calls this every few seconds. Handling those flipped the
							// parent back to "loading" (hiding its recovery UI) and
							// re-fired the error event on every tick: 122 events in one
							// 14-minute session on 2026-10-14. A late success still
							// arrives through `callback` above.
							if (cancelled) return;
							onToken?.(null);
							// Cloudflare passes an error code ("300xxx" generic/challenge,
							// "600xxx" interactive timeout, "110xxx" sitekey/domain,
							// "200100" wrong device clock). Capturing it turns a useless
							// "challenge_error" into an actionable root cause.
							if (errorCode === CLOCK_SKEW_ERROR_CODE) {
								handleFailure("clock_skew", { error_code: errorCode });
								return;
							}
							handleFailure("challenge_error", { error_code: errorCode });
						},
					});
					rendered = true;
					setWidgetStatus("rendered");
					posthogRef.current?.capture?.("turnstile_widget_rendered", {
						action,
						render_ms: Date.now() - mountedAt,
					});
					window.clearTimeout(timeoutId);
				} catch (err) {
					console.warn("[turnstile] render failed:", err);
					handleFailure("render_error", {
						message: err instanceof Error ? err.message : String(err),
					});
				}
			}

			function handleVisibilityChange() {
				if (document.visibilityState !== "visible" || !resetPendingOnVisible) {
					return;
				}
				resetPendingOnVisible = false;
				try {
					const ts = window.turnstile;
					if (ts && widgetIdRef.current) ts.reset(widgetIdRef.current);
				} catch {}
			}
			document.addEventListener("visibilitychange", handleVisibilityChange);

			startLoadTimeout();
			tryRender();

			return () => {
				cancelled = true;
				window.clearTimeout(timeoutId);
				for (const id of pollTimers) window.clearTimeout(id);
				for (const id of retryTimers) window.clearTimeout(id);
				document.removeEventListener(
					"visibilitychange",
					handleVisibilityChange,
				);
				const ts = window.turnstile;
				if (ts && widgetIdRef.current) {
					try {
						ts.remove(widgetIdRef.current);
					} catch {
						// Widget may already be detached; ignore.
					}
					widgetIdRef.current = null;
				}
			};
		}, [
			isDev,
			isProd,
			containerId,
			siteKey,
			action,
			theme,
			compact,
			onToken,
			onError,
			loadTimeoutMs,
			maxRetryAttempts,
			locale,
			surface,
			// Deliberately omitted: `posthog` is read through `posthogRef` (see
			// above) so this effect never tears down/re-mounts the widget purely
			// because the analytics client's identity changed.
		]);

		if (isDev) return null;

		return (
			<>
				<Script
					src={`${TURNSTILE_SCRIPT_SRC}?render=explicit`}
					strategy="afterInteractive"
					onError={() => {
						posthog?.capture?.("turnstile_widget_error", {
							reason: "script_load_error",
							action,
							locale,
							surface,
							error_code: null,
							browser_family: getBrowserFamily(),
						});
						// Immediately terminal — the script tag failed to load at all, so
						// there's no render loop to retry. Report it exactly like the
						// exhausted-retry-ladder case in `handleFailure`.
						if (isProd) {
							reportTerminalFailure({
								reason: "script_load_error",
								action,
								locale,
								surface,
								errorCode: null,
								browserFamily: getBrowserFamily(),
							});
						}
						// Was `onError?.("load_timeout")` — this is a script-load failure,
						// not a timeout. Keep the callback's reason in sync with the
						// PostHog capture above so a consumer bucketing by `reason`
						// (see `magic-link-form.tsx`) isn't fed a mislabeled cause.
						onError?.("script_load_error");
					}}
				/>
				<div
					id={containerId}
					// Reserve Cloudflare's own widget footprint (normal: 300x65,
					// compact: 130x120) so the container doesn't collapse to 0
					// height before the CDN script mounts the iframe, then jump
					// open — the CLS census flagged /id/apply (0.49 p75) and this
					// container had no size reservation at all. `appearance:
					// "interaction-only"` keeps the widget visually hidden most of
					// the time, so this trades a small permanent gap for avoiding
					// the shift on the (rarer) interactive-challenge path — best
					// effort from static review; needs field-data confirmation.
					className={`${compact ? "min-h-[120px]" : "min-h-[65px]"} ${className ?? ""}`}
					data-testid="turnstile-widget"
					data-turnstile-status={widgetStatus}
				/>
			</>
		);
	},
);

// Memoize so controlled-input forms (e.g. magic-link email field) don't pay
// for re-rendering the Turnstile mount on every keystroke. INP-sensitive.
export const TurnstileWidget = memo(TurnstileWidgetImpl);
